/**
 * Product Scroll Sequence
 * 滚动驱动序列帧动画：根据 track 内的滚动进度切换帧图，并控制两段文案的淡入淡出。
 * 帧索引与 fade 起止值均为序列内相对索引（0 = Start frame），由 Liquid 从绝对帧号换算。
 */
if (!customElements.get('product-scroll-sequence')) {
  class ProductScrollSequence extends HTMLElement {
    constructor() {
      super();
      this.currentFrame = 0;
      this.frameCache = new Map();
      this.handleScroll = this.requestUpdate.bind(this);
      this.handleResize = this.requestUpdate.bind(this);
      this.isTicking = false;
      this.hasPreloadedAll = false;
    }

    connectedCallback() {
      // 移动端使用静态的「上文下视频」布局，不启用滚动序列帧与资源预加载
      if (window.matchMedia('(max-width: 767px)').matches) {
        return;
      }

      this.image = this.querySelector('.product-scroll-sequence__image');
      this.track = this.querySelector('.product-scroll-sequence__track');
      this.sticky = this.querySelector('.product-scroll-sequence__sticky');
      this.primaryOverlay = this.querySelector('.product-scroll-sequence__overlay--primary');
      this.secondaryOverlay = this.querySelector('.product-scroll-sequence__overlay--secondary');

      const framesNode = this.querySelector('.product-scroll-sequence__frames-json');
      this.frameUrls = framesNode ? JSON.parse(framesNode.textContent.trim()) : [];
      this.totalFrames = this.frameUrls.length;

      if (!this.image || !this.track || !this.sticky || !this.totalFrames) {
        return;
      }

      // 文案透明度区间，来自 data-* 属性（Liquid 已减去 frame_start）
      this.primaryFadeStart = Number(this.dataset.primaryFadeStart || 0);
      this.primaryFadeEnd = Number(this.dataset.primaryFadeEnd || 2);
      this.secondaryFadeStart = Number(this.dataset.secondaryFadeStart || 12);
      this.secondaryFadeEnd = Number(this.dataset.secondaryFadeEnd || this.totalFrames - 1);

      this.frameCache.set(0, this.image);
      this.preloadNearbyFrames(0, 4);
      this.setupIntersectionObserver();

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }

    /**
     * 进入视口（含 300px 提前量）后预加载全部帧，避免首屏一次性拉取所有图片。
     */
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        this.preloadAllFrames();
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            this.preloadAllFrames();
            this.requestUpdate();
          });
        },
        {
          rootMargin: '300px 0px'
        }
      );

      this.intersectionObserver.observe(this);
    }

    /** 合并 scroll/resize 触发，每帧最多执行一次 update。 */
    requestUpdate() {
      if (this.isTicking) {
        return;
      }

      this.isTicking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.isTicking = false;
      });
    }

    /**
     * 将 track 内已滚动距离映射为帧索引。
     * track 高度 = scroll_height（如 400vh），sticky 区域固定不动，滚动发生在 track 内部。
     */
    update() {
      if (!this.totalFrames) {
        return;
      }

      const stickyHeight = this.sticky.offsetHeight || window.innerHeight;
      const maxScroll = Math.max(this.track.offsetHeight - stickyHeight, 1);
      const rect = this.track.getBoundingClientRect();
      // rect.top 为负表示 track 顶部已滚出视口上方
      const scrolled = this.clamp(-rect.top, 0, maxScroll);
      const progress = maxScroll === 0 ? 0 : scrolled / maxScroll;
      const frameFloat = progress * (this.totalFrames - 1);
      const frameIndex = Math.round(frameFloat);

      this.setFrame(frameIndex);
      this.updateOverlayOpacity(frameFloat);
    }

    setFrame(frameIndex) {
      const nextFrame = this.clamp(frameIndex, 0, this.totalFrames - 1);

      if (nextFrame === this.currentFrame) {
        return;
      }

      this.currentFrame = nextFrame;
      this.image.src = this.frameUrls[nextFrame];
      this.preloadNearbyFrames(nextFrame, 4);
    }

    /**
     * Primary：fadeStart 前完全显示，fadeStart–fadeEnd 线性淡出。
     * Secondary：fadeStart–fadeEnd 线性淡入，fadeEnd 后完全显示。
     * 使用 frameFloat（非取整帧）保证透明度与滚动进度平滑同步。
     */
    updateOverlayOpacity(frameFloat) {
      if (this.primaryOverlay) {
        let primaryOpacity = 1;

        if (frameFloat >= this.primaryFadeStart) {
          primaryOpacity = 1 - this.normalize(frameFloat, this.primaryFadeStart, this.primaryFadeEnd);
        }

        this.primaryOverlay.style.opacity = primaryOpacity.toFixed(3);
      }

      if (this.secondaryOverlay) {
        const secondaryOpacity = this.normalize(frameFloat, this.secondaryFadeStart, this.secondaryFadeEnd);
        this.secondaryOverlay.style.opacity = secondaryOpacity.toFixed(3);
      }
    }

    preloadNearbyFrames(centerIndex, radius) {
      for (let index = centerIndex - radius; index <= centerIndex + radius; index += 1) {
        this.preloadFrame(index);
      }
    }

    preloadAllFrames() {
      if (this.hasPreloadedAll) {
        return;
      }

      this.hasPreloadedAll = true;
      this.frameUrls.forEach((_, index) => {
        this.preloadFrame(index);
      });
    }

    preloadFrame(index) {
      if (index < 0 || index >= this.totalFrames || this.frameCache.has(index)) {
        return;
      }

      const img = new Image();
      img.decoding = 'async';
      img.src = this.frameUrls[index];
      this.frameCache.set(index, img);
    }

    /** 将 value 映射到 [start, end] 区间的 0–1 进度。 */
    normalize(value, start, end) {
      if (end <= start) {
        return value >= end ? 1 : 0;
      }

      return this.clamp((value - start) / (end - start), 0, 1);
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }
  }

  customElements.define('product-scroll-sequence', ProductScrollSequence);
}
